package com.tovtalk.ui.components

import androidx.compose.animation.animateColorAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsFocusedAsState
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Search
import androidx.compose.material.icons.filled.Visibility
import androidx.compose.material.icons.filled.VisibilityOff
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import com.tovtalk.ui.theme.TovTalkDurations
import com.tovtalk.ui.theme.TovTalkEasings
import com.tovtalk.ui.theme.TovTalkTheme
import com.tovtalk.utils.strings.StringProvider

/**
 * TovTalk Text Field - Clean, minimal text input with focus animations
 */
@Composable
fun TovTalkTextField(
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    placeholder: String = "",
    label: String? = null,
    leadingIcon: ImageVector? = null,
    trailingIcon: @Composable (() -> Unit)? = null,
    isError: Boolean = false,
    errorMessage: String? = null,
    enabled: Boolean = true,
    singleLine: Boolean = true,
    maxLines: Int = if (singleLine) 1 else Int.MAX_VALUE,
    keyboardOptions: KeyboardOptions = KeyboardOptions.Default,
    keyboardActions: KeyboardActions = KeyboardActions.Default,
    visualTransformation: VisualTransformation = VisualTransformation.None,
) {
    val interactionSource = remember { MutableInteractionSource() }
    val isFocused by interactionSource.collectIsFocusedAsState()

    val borderColor by animateColorAsState(
        targetValue = when {
            isError -> TovTalkTheme.colors.error
            isFocused -> TovTalkTheme.colors.brand
            else -> TovTalkTheme.colors.border
        },
        animationSpec = tween(
            durationMillis = TovTalkDurations.fast,
            easing = TovTalkEasings.standard
        ),
        label = "borderColor"
    )

    Column(modifier = modifier) {
        if (label != null) {
            Text(
                text = label,
                style = TovTalkTheme.typography.labelMedium,
                color = if (isError) TovTalkTheme.colors.error else TovTalkTheme.colors.textSecondary,
                modifier = Modifier.padding(bottom = TovTalkTheme.spacing.xs)
            )
        }

        BasicTextField(
            value = value,
            onValueChange = onValueChange,
            modifier = Modifier
                .fillMaxWidth()
                .height(TovTalkTheme.sizes.buttonHeightMd)
                .clip(TovTalkTheme.shapes.input)
                .background(TovTalkTheme.colors.surfaceOverlay)
                .border(
                    width = TovTalkTheme.sizes.borderWidth,
                    color = borderColor,
                    shape = TovTalkTheme.shapes.input
                ),
            enabled = enabled,
            textStyle = TovTalkTheme.typography.bodyMedium.copy(
                color = TovTalkTheme.colors.textPrimary
            ),
            cursorBrush = SolidColor(TovTalkTheme.colors.brand),
            singleLine = singleLine,
            maxLines = maxLines,
            keyboardOptions = keyboardOptions,
            keyboardActions = keyboardActions,
            visualTransformation = visualTransformation,
            interactionSource = interactionSource,
            decorationBox = { innerTextField ->
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = TovTalkTheme.spacing.inputPadding),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    if (leadingIcon != null) {
                        Icon(
                            imageVector = leadingIcon,
                            contentDescription = null,
                            modifier = Modifier.size(TovTalkTheme.sizes.iconMd),
                            tint = if (isFocused) TovTalkTheme.colors.brand else TovTalkTheme.colors.textTertiary
                        )
                        Spacer(modifier = Modifier.width(TovTalkTheme.spacing.sm))
                    }

                    Box(modifier = Modifier.weight(1f)) {
                        if (value.isEmpty()) {
                            Text(
                                text = placeholder,
                                style = TovTalkTheme.typography.bodyMedium,
                                color = TovTalkTheme.colors.textTertiary
                            )
                        }
                        innerTextField()
                    }

                    if (trailingIcon != null) {
                        Spacer(modifier = Modifier.width(TovTalkTheme.spacing.sm))
                        trailingIcon()
                    }
                }
            }
        )

        if (isError && !errorMessage.isNullOrBlank()) {
            Text(
                text = errorMessage,
                style = TovTalkTheme.typography.caption,
                color = TovTalkTheme.colors.error,
                modifier = Modifier.padding(top = TovTalkTheme.spacing.xxs, start = TovTalkTheme.spacing.xxs)
            )
        }
    }
}

/**
 * Search Field - Specialized for search
 */
@Composable
fun TovTalkSearchField(
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    placeholder: String = StringProvider.get("placeholder_search"),
    onSearch: (() -> Unit)? = null,
) {
    TovTalkTextField(
        value = value,
        onValueChange = onValueChange,
        modifier = modifier,
        placeholder = placeholder,
        leadingIcon = Icons.Default.Search,
        singleLine = true,
        keyboardOptions = KeyboardOptions(
            imeAction = ImeAction.Search
        ),
        keyboardActions = KeyboardActions(
            onSearch = { onSearch?.invoke() }
        )
    )
}

/**
 * Password Field - With visibility toggle
 */
@Composable
fun TovTalkPasswordField(
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    placeholder: String = StringProvider.get("placeholder_password"),
    label: String? = null,
    isError: Boolean = false,
    errorMessage: String? = null,
) {
    var passwordVisible by remember { mutableStateOf(false) }

    TovTalkTextField(
        value = value,
        onValueChange = onValueChange,
        modifier = modifier,
        placeholder = placeholder,
        label = label,
        isError = isError,
        errorMessage = errorMessage,
        visualTransformation = if (passwordVisible) VisualTransformation.None else PasswordVisualTransformation(),
        keyboardOptions = KeyboardOptions(
            keyboardType = KeyboardType.Password
        ),
        trailingIcon = {
            IconButton(
                onClick = { passwordVisible = !passwordVisible },
                modifier = Modifier.size(TovTalkTheme.sizes.iconMd)
            ) {
                Icon(
                    imageVector = if (passwordVisible) Icons.Default.Visibility else Icons.Default.VisibilityOff,
                    contentDescription = if (passwordVisible) StringProvider.get("accessibility_hide_password") else StringProvider.get("accessibility_show_password"),
                    tint = TovTalkTheme.colors.textTertiary
                )
            }
        }
    )
}
